'use client'

import Icon from '@susu/headless-commerce/components/Icon/Icon'
import { useCountry } from '@susu/headless-commerce/contexts/country/client'
import { useLocale } from '@susu/headless-commerce/contexts/locale/client'
import { useClientNav } from '@susu/headless-commerce/features/navigation/components/ClientNavProvider'
import { usePageType } from '@susu/headless-commerce/hooks/usePageType'
import { getLanguageFromLocale } from '@susu/headless-commerce/utils/localeUtils'
import { URLData } from '@susu/headless-commerce/utils/tracking/segment'
import { trackCountrySelectorClicked } from '@susu/headless-commerce/utils/tracking/trackCountrySelectorClicked'
import { trackLanguageSelectorClicked } from '@susu/headless-commerce/utils/tracking/trackLanguageSelectorClicked'
import classNames from 'classnames'
import { useCallback } from 'react'
import styles from './LocaleDisplay.module.scss'

export type ClientLocaleDisplayProps = {
  colorPalette?: 'light' | 'dark'
  isInsideFooter?: boolean
  showIcon?: boolean
}

export default function ClientLocaleDisplay({
  colorPalette = 'dark',
  isInsideFooter = false,
  showIcon = false,
}: Readonly<ClientLocaleDisplayProps>) {
  const country = useCountry()
  const locale = useLocale()
  const urlLocale = getLanguageFromLocale(locale)
  const [baseLang] =
    country.languages.indexOf(urlLocale) > -1 ? [urlLocale] : ['en']
  const languageNames = new Intl.DisplayNames([baseLang], {
    type: 'language',
  })
  const language = languageNames.of(baseLang)
  const { openNav, openCountrySelector, openLanguageSelector } = useClientNav()
  const { url, locationId } = URLData()
  const pageType = usePageType()

  const handleCountryClick = useCallback(() => {
    openNav()
    openCountrySelector(true)

    trackCountrySelectorClicked({
      country,
      eventLocation: 'footer',
      locale,
      locationId,
      pageType,
      url,
    })
  }, [openNav, openCountrySelector, country, locale, locationId, pageType, url])

  const handleLanguageClick = useCallback(() => {
    openNav()
    openLanguageSelector(true)

    trackLanguageSelectorClicked({
      country,
      eventLocation: 'footer',
      locale,
      locationId,
      pageType,
      url,
    })
  }, [
    openNav,
    openLanguageSelector,
    country,
    locale,
    locationId,
    pageType,
    url,
  ])

  const countrySelectorClasses = classNames(
    styles['locale-display__button'],
    'caption-regular',
    {
      [styles['locale-display--light']]: colorPalette === 'light',
      [styles['locale-display--dark']]: colorPalette === 'dark',
    },
  )
  const localeDisplaySpanClasses = classNames(
    { [styles['locale-display__span--footer']]: isInsideFooter },
    styles['locale-display__span'],
    'caption-light',
  )

  return (
    <div className={`${styles['locale-display']}`}>
      {showIcon && (
        <span className={styles.icon}>
          <Icon icon="location" aria-hidden="true" />
        </span>
      )}
      <button
        type="button"
        className={countrySelectorClasses}
        onClick={handleCountryClick}
        data-testid="button_country-selector"
      >
        {country.name}
      </button>
      {country.languages.length > 1 ? (
        <button
          type="button"
          className={`${styles['locale-display__button']} caption-light ${
            colorPalette === 'light'
              ? styles['locale-display--light']
              : styles['locale-display--dark']
          }`}
          onClick={handleLanguageClick}
          data-testid="button_language-selector"
        >
          {language}
        </button>
      ) : (
        <span className={localeDisplaySpanClasses}>{language}</span>
      )}
    </div>
  )
}
